/**
 * Getting from a requirement row to the control that satisfies it.
 *
 * OnboardingPage provides `jump`; RequirementList offers a button per row
 * (requirementActions.ts) that calls it. The page switches stage, the stage
 * opens whatever it must (`prepare`), and the target is brought into view and
 * focused — or pressed, when it is the button that opens a form.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import type { Jump } from './requirementActions';

export interface PendingJump extends Jump { nonce: number }

export const JumpContext = createContext<{
  orgId: string;
  jump: (j: Jump) => void;
  pending: PendingJump | null;
} | null>(null);

export const useJump = () => useContext(JumpContext);

/** Run `open` when a jump asks this stage to prepare `what` (once per jump). */
export function usePrepare(match: (prepare: string) => boolean, open: (prepare: string) => void) {
  const ctx = useJump();
  const p = ctx?.pending;
  useEffect(() => {
    if (p?.prepare && match(p.prepare)) open(p.prepare);
    // Keyed by the jump, not the callbacks: each jump prepares once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.nonce]);
}

/**
 * Owns the pending jump and lands it: waits for the target to exist (the
 * stage, or an editor loading its data, may still be rendering), then scrolls
 * it into view and focuses or presses it. Gives up quietly after three seconds.
 */
export function useJumpState(onStage: (stage: Jump['stage']) => void) {
  const [pending, setPending] = useState<PendingJump | null>(null);

  const jump = (j: Jump) => {
    onStage(j.stage);
    setPending({ ...j, nonce: Date.now() });
  };

  useEffect(() => {
    if (!pending) return undefined;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const el = document.getElementById(pending.target);
      if (el) {
        window.clearInterval(timer);
        el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        if (pending.click) el.click();
        else el.focus({ preventScroll: true });
      } else if (Date.now() - started > 3000) {
        window.clearInterval(timer);
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [pending]);

  return { jump, pending };
}
